import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Clock } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import autosurLogo from "@/assets/autosur-logo-full.webp";

const navLinks = [
  { href: "/", label: "Accueil" },
  { href: "/le-centre", label: "Le Centre" },
  { href: "/prestations", label: "Prestations" },
  { 
    label: "Véhicules", 
    children: [
      { href: "/vehicules/voitures", label: "Voitures" },
      { href: "/vehicules/motos", label: "Motos & 2 Roues" },
      { href: "/vehicules/collection", label: "Collection & Anciennes" },
      { href: "/vehicules/utilitaires", label: "Utilitaires & Camping-cars" },
    ]
  },
  { href: "/faq", label: "FAQ" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" },
];

export const SiteHeader = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [vehiculesOpen, setVehiculesOpen] = useState(false);
  const location = useLocation();

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      {/* Top bar */}
      <div className="hidden lg:block bg-autosur-navy text-white py-2">
        <div className="container mx-auto px-6 flex items-center justify-between text-sm font-body">
          <div className="flex items-center gap-6">
            <a href="tel:+33149889240" className="flex items-center gap-2 hover:text-white/80 transition-colors">
              <Phone className="w-4 h-4" />
              01 49 88 92 40
            </a>
            <a href="https://maps.app.goo.gl/QMbfid7TsPVr8ZB76" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <MapPin className="w-4 h-4" />
              133, Bd Aristide Briand - 93100 Montreuil-sous-Bois
            </a>
          </div>
          <a href="https://controle-technique.autosur.fr/550-autosur-montreuil-sous-bois#in-nav-rdv" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
            <Clock className="w-4 h-4" />
            Lun-Ven: 8h30-18h30 | Sam: 9h-13h
          </a>
        </div>
      </div>

      {/* Main header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-border">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex-shrink-0"
              onClick={(e) => {
                if (location.pathname === "/") {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
            >
              <img 
                src={autosurLogo} 
                alt="Autosur Montreuil-sous-Bois" 
                className="h-8 sm:h-10 w-auto"
                width={105}
                height={32}
              />
            </Link>

            {/* Desktop navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                link.children ? (
                  <div 
                    key={link.label}
                    className="relative group"
                    onMouseEnter={() => setVehiculesOpen(true)}
                    onMouseLeave={() => setVehiculesOpen(false)}
                  >
                    <button className="px-4 py-2 font-heading font-semibold text-sm text-autosur-navy hover:text-autosur-blue transition-colors flex items-center gap-1">
                      {link.label}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <AnimatePresence>
                      {vehiculesOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute top-full left-0 w-56 bg-white shadow-lg rounded-lg border border-border py-2 z-50"
                        >
                          {link.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className={`block px-4 py-2.5 font-body text-sm transition-colors ${
                                isActive(child.href)
                                  ? 'text-autosur-blue bg-autosur-blue/5'
                                  : 'text-autosur-navy hover:text-autosur-blue hover:bg-gray-50'
                              }`}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={(e) => {
                      if (link.href === "/" && location.pathname === "/") {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }
                    }}
                    className={`px-4 py-2 font-heading font-semibold text-sm transition-colors ${
                      isActive(link.href)
                        ? 'text-autosur-blue'
                        : 'text-autosur-navy hover:text-autosur-blue'
                    }`}
                  >
                    {link.label}
                  </Link>
                )
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link
                to="/contact"
                className="px-6 py-2.5 bg-autosur-red text-white font-heading font-semibold text-sm rounded-full hover:bg-autosur-red-light transition-colors shadow-md"
              >
                Prendre RDV
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-autosur-navy"
              aria-label="Menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu - Full screen */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 top-16 sm:top-20 bg-white z-50 overflow-y-auto"
            >
              <nav className="container mx-auto px-4 py-6 flex flex-col min-h-full">
                <div className="flex-1 space-y-1">
                  {navLinks.map((link) => (
                    link.children ? (
                      <div key={link.label}>
                        <button 
                          onClick={() => setVehiculesOpen(!vehiculesOpen)}
                          className="w-full flex items-center justify-between px-4 py-3 font-heading font-semibold text-autosur-navy text-lg"
                        >
                          {link.label}
                          <svg className={`w-5 h-5 transition-transform ${vehiculesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <AnimatePresence>
                          {vehiculesOpen && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="pl-4 space-y-1 overflow-hidden"
                            >
                              {link.children.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  onClick={() => setIsOpen(false)}
                                  className={`block px-4 py-2.5 font-body text-base rounded-lg ${
                                    isActive(child.href)
                                      ? 'text-autosur-blue bg-autosur-blue/5'
                                      : 'text-autosur-navy hover:bg-gray-50'
                                  }`}
                                >
                                  {child.label}
                                </Link>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={(e) => {
                          if (link.href === "/" && location.pathname === "/") {
                            e.preventDefault();
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }
                          setIsOpen(false);
                        }}
                        className={`block px-4 py-3 font-heading font-semibold text-lg rounded-lg ${
                          isActive(link.href)
                            ? 'text-autosur-blue bg-autosur-blue/5'
                            : 'text-autosur-navy hover:bg-gray-50'
                        }`}
                      >
                        {link.label}
                      </Link>
                    )
                  ))}
                </div>
                
                {/* Mobile contact info - Fixed at bottom */}
                <div className="pt-6 mt-6 border-t space-y-4">
                  <a 
                    href="tel:+33149889240" 
                    className="flex items-center gap-3 px-4 py-2 text-autosur-navy font-body text-lg"
                  >
                    <Phone className="w-6 h-6 text-autosur-red" />
                    01 49 88 92 40
                  </a>
                  <Link
                    to="/contact"
                    onClick={() => setIsOpen(false)}
                    className="block mx-4 py-4 bg-autosur-red text-white font-heading font-semibold text-center text-lg rounded-full"
                  >
                    Prendre RDV
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
